import { useEffect, useRef, useState } from "react";

import {
  AnchorButton,
  AnchorButtonProps,
  Button,
  ButtonProps,
  Icon,
  Intent,
} from "@blueprintjs/core";
import styled from "@emotion/styled";

import { copyToClipboard } from "@/components/helpers/browser/copy-to-clipboard";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { Facebook, LinkedIn, Twitter } from "@/components/pieces/icons/Icons";
import { CTA_COLOR } from "@/css/constants";
import { ReferralUser } from "@/types/users";

export function SharingOptions({ user }: { user?: ReferralUser | null }) {
  const [copied, setCopied] = useState(false);
  const mountedRef = useRef(false);
  const url = `${window.location.origin.replace("pro", "www")}/?refcode=${
    user?.referralCode
  }`;
  const twitterMessage = `Want to grow your career? Use @placement to get find a life changing coach!\n\n${url}`;
  const linkedInComponents = {
    title: "Placement – Grow your career with a coach",
    summary:
      "Access career coaching, game-changing content, and magic tools at Placement.",
  };

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        if (mountedRef.current) {
          setCopied(false);
        }
      }, 5000);
    }
  }, [copied]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <>
      <Box fontSize={22} fontWeight={600}>
        Share Your Link
      </Box>
      <Flex
        mt={3}
        backgroundColor="#FFF"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <SharingOption mb={3}>
          <ActionButton
            onClick={async () => {
              await copyToClipboard(url);
              setCopied(true);
            }}
            icon={
              <Icon
                color={copied ? undefined : CTA_COLOR}
                icon={copied ? "tick" : "clipboard"}
              />
            }
            intent={copied ? Intent.PRIMARY : undefined}
          >
            {copied ? "Copied!" : "Copy to Clipboard"}
          </ActionButton>
        </SharingOption>
        <SharingOption mb={3}>
          <ActionAnchorButton
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
              url,
            )}&title=${encodeURIComponent(
              linkedInComponents.title,
            )}&summary=${encodeURIComponent(linkedInComponents.summary)}`}
            icon={<LinkedIn color={CTA_COLOR} size={20} />}
            target="_blank"
          >
            Share on LinkedIn
          </ActionAnchorButton>
        </SharingOption>
        <SharingOption mb={3}>
          <ActionAnchorButton
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              url,
            )}`}
            icon={<Facebook color={CTA_COLOR} size={20} />}
            target="_blank"
          >
            Share on Facebook
          </ActionAnchorButton>
        </SharingOption>
        <SharingOption mb={3}>
          <ActionAnchorButton
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
              twitterMessage,
            )}`}
            icon={<Twitter color={CTA_COLOR} size={20} />}
            target="_blank"
          >
            Share on Twitter
          </ActionAnchorButton>
        </SharingOption>
        {navigator.share ? (
          <SharingOption mb={3}>
            <ActionButton
              onClick={() => {
                navigator.share({
                  title: "Placement: Fly through your job search",
                  url,
                });
              }}
              icon={<Icon color={CTA_COLOR} icon={"plus"} />}
            >
              More Options
            </ActionButton>
          </SharingOption>
        ) : null}
      </Flex>
    </>
  );
}

function ActionButton(props: ButtonProps) {
  return (
    <Button
      style={{
        borderRadius: 8,
        height: 48,
        paddingLeft: 16,
      }}
      fill={true}
      large={true}
      alignText="left"
      {...props}
    />
  );
}

function ActionAnchorButton(props: AnchorButtonProps) {
  return (
    <AnchorButton
      style={{
        borderRadius: 8,
        height: 48,
        paddingLeft: 16,
      }}
      fill={true}
      large={true}
      alignText="left"
      {...props}
    />
  );
}

const SharingOption = styled(Box)`
  flex: 0 calc(50% - 8px);

  @media screen and (max-width: ${breakpoints.md - 1}px) {
    flex-basis: 100%;
  }
`;
